import React, { useEffect, useState } from "react";
import { Drawer, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";
import CommonInput from "../input";
import CommonButton from "../button";
import PermissionChecklist from "./PermissionChecklist";

const useStyles = makeStyles(() => ({
  paper: {
    width: "520px",
    maxWidth: "100vw",
    padding: "28px 32px 40px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  title: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "22px",
  },
  subtitle: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
    marginBottom: "22px",
  },
  sectionLabel: {
    fontSize: "16px",
    color: theme.colors.Violet,
    marginBottom: "5px",
    marginTop: "6px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
  },
  hint: {
    fontSize: "12px",
    color: theme.colors.Violet,
    opacity: 0.6,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    marginBottom: "10px",
  },
  fieldError: {
    color: theme.colors.Pomegranate,
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    margin: "6px 0 12px",
  },
  actions: { display: "flex", gap: "12px", marginTop: "22px" },
}));

const emptyForm = { name: "", description: "", permissions: [] };

const serverError = (fieldErrors, key) => {
  const val = fieldErrors?.[key];
  if (!val) return "";
  if (Array.isArray(val)) return val.filter(Boolean).join(" ");
  if (typeof val === "string") return val;
  return "";
};

function GroupFormDrawer({
  open,
  mode = "create",
  group,
  catalogue = [],
  catalogueLoading = false,
  loading = false,
  fieldErrors,
  onSubmit,
  onClose,
}) {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm);
  const [clientErrors, setClientErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && group) {
      setForm({
        name: group.name || "",
        description: group.description || "",
        permissions: Array.isArray(group.permissions) ? [...group.permissions] : [],
      });
    } else {
      setForm(emptyForm);
    }
    setClientErrors({});
  }, [open, mode, group]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    const errors = {};
    if (!form.name || !form.name.trim()) errors.name = "Name is required.";
    else if (form.name.trim().length > 48) errors.name = "Name must be 48 characters or fewer.";
    if (form.description && form.description.length > 96)
      errors.description = "Description must be 96 characters or fewer.";
    setClientErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (loading) return;
    if (!validate()) return;
    // PATCH replaces the whole permission set — always submit the full desired list.
    onSubmit({
      name: form.name.trim(),
      description: form.description?.trim() ? form.description.trim() : "",
      permissions: form.permissions,
    });
  };

  const errFor = (key) => clientErrors[key] || serverError(fieldErrors, key);
  const permissionsError = serverError(fieldErrors, "permissions");

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={loading ? undefined : onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.title}>
          {mode === "edit" ? "Edit role group" : "Create role group"}
        </div>
        <IconButton size="small" onClick={onClose} disabled={loading} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.subtitle}>
        {mode === "edit"
          ? "Update this group's details and capabilities."
          : "Define a backoffice role and the capabilities it grants."}
      </div>

      <CommonInput
        label="Name"
        placeholder="e.g. Support"
        value={form.name}
        onChange={(e) => setField("name", e.target.value)}
        error={!!errFor("name")}
        helperText={errFor("name")}
      />

      <CommonInput
        label="Description"
        placeholder="Optional — what this role is for"
        multiline
        minRows={2}
        value={form.description}
        onChange={(e) => setField("description", e.target.value)}
        error={!!errFor("description")}
        helperText={errFor("description")}
      />

      <div className={classes.sectionLabel}>Permissions</div>
      <div className={classes.hint}>
        Select the capabilities this role grants. “dashboard-only” items control
        nav visibility, not backend access.
      </div>
      {permissionsError ? (
        <div className={classes.fieldError}>{permissionsError}</div>
      ) : null}

      <PermissionChecklist
        catalogue={catalogue}
        loading={catalogueLoading}
        value={form.permissions}
        onChange={(codes) => setField("permissions", codes)}
      />

      <div className={classes.actions}>
        <CommonButton
          type="button"
          text="Cancel"
          stylevariant="secondary2"
          fontSize="15px"
          disabled={loading}
          onClick={onClose}
        />
        <CommonButton
          type="button"
          text={mode === "edit" ? "Save changes" : "Create group"}
          stylevariant="primary"
          fontSize="15px"
          loading={loading}
          disabled={loading || !form.name.trim()}
          onClick={handleSubmit}
        />
      </div>
    </Drawer>
  );
}

export default GroupFormDrawer;
